import { useEffect, useState } from "react"

import isEqual from "lodash-es/isEqual"

import { useEvent } from "./useEvent"

export interface UseControlledStateArgs<T> {
  value?: T | undefined | null
  defaultValue?: T | undefined | null
  resetOnDefaultChange?: boolean
  onChange?: (value: T) => void
}

/**
 * Hook for handling state in both controlled and uncontrolled scenarios.
 *
 * @template T - The type of the state value.
 * @param {UseControlledStateArgs<T>} [args] - Optional arguments for configuring the controlled state.
 * @param {T} args.value - Optional. The state value.
 * @param {T} args.defaultValue - Optional. The default value.
 * @param {boolean} args.resetOnDefaultChange - Optional. default: `false`. If true the value will be reset to the default when it changes.
 * @param {(value: T) => void} args.onChange - Callback to be called when the state value changes.
 * @returns {[T, (value: T) => void]} - A tuple containing the current state value and a function to update the state.
 */
export function useControlledState<T>(args?: UseControlledStateArgs<T>) {
  const {
    value,
    defaultValue,
    resetOnDefaultChange = false,
    onChange
  } = args || {}
  const [internalValue, setInternalValue] = useState<T | null | undefined>(
    value || defaultValue
  )
  const handleChange = useEvent((nextValue: T) => {
    if (onChange) {
      onChange?.(nextValue)
    }
    setInternalValue(nextValue)
  })

  useEffect(() => {
    if (value !== undefined && !isEqual(value, internalValue)) {
      setInternalValue(value)
    }
  }, [value])

  useEffect(() => {
    if (
      resetOnDefaultChange &&
      defaultValue !== undefined &&
      !isEqual(defaultValue, internalValue)
    ) {
      setInternalValue(defaultValue)
    }
  }, [defaultValue, resetOnDefaultChange])

  return [value !== undefined ? value : internalValue, handleChange] as [
    T,
    (value: T) => void
  ]
}
